import * as B from '@blueprintjs/core';
import React from 'react';
import MultiLineEllipsis from 'react-dotdotdot';
import {Link} from 'react-router-dom';
import {Row} from 'react-table';

import FeatureMiniMap from 'app/components/FeatureMiniMap/FeatureMiniMap';
import {getIsSomeSelected, getIsSubRow} from 'app/pages/PropertyOverview/PropertyTableSelection';
import {PropertyTableRow} from 'app/pages/PropertyOverview/PropertyTableTypes';
import * as CONSTANTS from 'app/utils/constants';
import {getMultiFeaturePropertyParts} from 'app/utils/multiFeaturePropertyUtils';
import * as routeUtils from 'app/utils/routeUtils';

import cs from './PropertyOverview.module.scss';
import {InputWrapper} from './PropertyTable';

export interface PropertySelectionCellProps {
  row: Row<PropertyTableRow>;
  allowSelection: boolean;
  showExpandButton?: boolean;
  className?: string;
}

/**
 * Shared selection cell component that handles checkbox state including
 * indeterminate state for multi-location properties with partial selection.
 */
export const PropertySelectionCell: React.FunctionComponent<
  React.PropsWithChildren<PropertySelectionCellProps>
> = ({row, allowSelection, showExpandButton = false, className}) => {
  const isExpanded = row.canExpand && row.isExpanded;
  const isCollapsed = row.canExpand && !row.isExpanded;

  return (
    <div className={className} style={!allowSelection ? {visibility: 'hidden'} : {}}>
      {showExpandButton && (
        <>
          {getIsSubRow(row) ? (
            <B.Icon icon="key-enter" style={{transform: 'scaleX(-1)', marginRight: '7px'}} />
          ) : (
            <B.Button
              small
              minimal
              icon={isExpanded ? 'chevron-down' : isCollapsed ? 'chevron-right' : 'blank'}
              style={{...(!row.canExpand && {visibility: 'hidden'})}}
              onClick={(e) => {
                e.stopPropagation();
                row.canExpand && row.toggleExpanded();
              }}
              title={isExpanded ? 'Collapse' : isCollapsed ? 'Expand' : undefined}
            />
          )}
        </>
      )}

      <InputWrapper
        tabIndex={-1}
        disabled={!allowSelection}
        onChange={() => row.toggleRowSelected(!row.isSelected)}
        state={getIsSomeSelected(row) ? 'indeterminate' : row.isSelected ? 'checked' : 'unchecked'}
      />
    </div>
  );
};

export interface PropertyNameCellProps {
  row: Row<PropertyTableRow>;
  organizationId: string;
  projectId: string;
  isSubRow?: boolean;
  rows?: Row<PropertyTableRow>[];
  showProcessingStatus?: boolean;
  showFeatureIdInTitle?: boolean;
  className?: string;
}

/**
 * Shared property name cell component that handles:
 * - LOCATE links for properties with LOCATELink property
 * - Multi-location property part names with mini-map popover
 * - Processing status indicator
 * - Link to property dashboard
 */
export const PropertyNameCell: React.FunctionComponent<
  React.PropsWithChildren<PropertyNameCellProps>
> = ({
  row,
  organizationId,
  projectId,
  rows,
  showProcessingStatus = true,
  showFeatureIdInTitle = false,
}) => {
  const {features} = row.original;

  const href = routeUtils.makeProjectDashboardUrl(
    organizationId,
    projectId,
    'map',
    features.toArray()
  );

  const locateLink = features.first().getIn(['properties', 'LOCATELink'] as any) as
    | string
    | undefined;

  const propertyName = features.first().getIn(['properties', 'name']);
  const featureId = features.first().get('id');

  let title = propertyName,
    name: React.ReactNode = propertyName;

  // Show feature ID if toggled on
  if (showFeatureIdInTitle) {
    const displayText = `${propertyName} (${featureId})`;
    title = displayText;
    name = `${propertyName} (${featureId})`;
  }

  const isProcessing =
    features.first().getIn(['properties', 'status']) ===
    CONSTANTS.FEATURE_WAITING_FOR_PROCESSING_STATUS;

  // Handle multi-location part name rendering
  if (getIsSubRow(row) && rows) {
    const parentRow = rows.find((r) => r.depth === 0 && r.id.includes(row.id));
    if (parentRow) {
      const parentFeatures = parentRow.original.features;

      const multiFeaturePropertyParts = getMultiFeaturePropertyParts(
        parentFeatures,
        features.first()
      );

      const activePart = multiFeaturePropertyParts.find((p) => p.isActive);

      if (activePart) {
        const hasPartName = !!activePart.partName;
        title = (activePart.partName as string) || 'Location';
        if (showFeatureIdInTitle) {
          title = `${title} (${featureId})`;
        }
        name = (
          <B.Popover
            interactionKind="hover"
            content={
              <FeatureMiniMap
                features={parentFeatures}
                selectedFeatureId={activePart.feature.get('id')}
                setSelectedFeatureId={() => {}}
                hoveredFeatureId={null}
                hoverFeatureById={() => {}}
                height={200}
                width={200}
                padding={10}
              />
            }
          >
            {hasPartName ? title : <i>{title}</i>}
          </B.Popover>
        );
      }
    }
  }

  return (
    <div style={{display: 'flex', alignItems: 'center'}}>
      {/* Hack for SPNHF to show a link to LOCATE */}
      {locateLink && (
        <B.Tooltip content="View in LOCATE" position="top">
          <B.AnchorButton
            icon="link"
            href={locateLink}
            target="_blank"
            minimal
            onClick={(ev) => ev.stopPropagation()}
          />
        </B.Tooltip>
      )}

      <Link to={href} title={title}>
        <MultiLineEllipsis clamp={3}>{name}</MultiLineEllipsis>
      </Link>

      {showProcessingStatus &&
        (isProcessing && !row.isExpanded ? (
          <B.Tooltip content={'Imagery is loading'} position={'bottom'}>
            <span className={cs.cellProcessingState}>
              <B.Icon intent={B.Intent.WARNING} icon="time" size={16} />
            </span>
          </B.Tooltip>
        ) : (
          <span className={cs.cellProcessingState}>
            <B.Icon icon="blank" size={16} />
          </span>
        ))}
    </div>
  );
};
